import { apexDomain, go, webUrl } from '@martincastroalvarez/runtime';
import { useCallback, useEffect, useState } from 'react';
import { useParam } from './useParam';
import { safeNext } from './next';
import {
  openSession,
  SessionRefusedError,
  type OpenedSession,
  type SessionRequest,
} from '@martincastroalvarez/login/public';
import { useTracker } from '@martincastroalvarez/tracking/useTracker';
import { AUTHENTICATION, LoginEvents } from './events';
import { useLanguage } from './useLanguage';
import { format, type Copy } from './i18n';

/**
 * A refused `POST /v1/session`, worded: the API's JSON `{ message }` or plain text when it wrote one.
 * 502/503 often return HTML without CORS — the browser surfaces "Failed to fetch"; when a body
 * exists, show the server message.
 *
 * ⚠️ A message the SERVER sent is shown verbatim, in whatever language the API wrote it. Only the
 * messages this file invents are translated: putting the API's prose through a table here would
 * mean guessing which of its sentences it was, and a wrong guess is a wrong explanation.
 */
function sessionErrorMessage(refusal: SessionRefusedError, copy: Copy): string {
  const code = refusal.status;
  const raw = refusal.body;
  try {
    const parsed = JSON.parse(raw) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    // not JSON
  }
  const trimmed = raw.trim();
  if (trimmed && trimmed.length < 400 && !trimmed.startsWith('<')) {
    return trimmed;
  }
  if (code === 502 || code === 503 || code === 504) {
    return copy.unavailable;
  }
  return format(copy.failedWithCode, { code });
}

/**
 * `openSession` (the login connector's public entry), with a refusal worded for the page and
 * tracked once. A network failure passes through for `catchMessage` to word.
 */
async function signIn(
  request: SessionRequest,
  copy: Copy,
  track: ReturnType<typeof useTracker>['track'],
): Promise<OpenedSession> {
  try {
    return await openSession(request);
  } catch (err) {
    if (!(err instanceof SessionRefusedError)) throw err;
    const msg = sessionErrorMessage(err, copy);
    track({ name: LoginEvents.OAUTH_ERROR, category: AUTHENTICATION, label: msg.slice(0, 120) });
    throw new Error(msg);
  }
}

function catchMessage(err: unknown, copy: Copy): string {
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    return copy.unreachable;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return copy.failed;
}

interface UseLoginResult {
  isLoading: boolean;
  error: string | null;
  verify: (token: string) => Promise<void>;
  verifyLinkedIn: (code: string, redirectUri: string) => Promise<void>;
  verifyFacebook: (code: string, redirectUri: string) => Promise<void>;
  /**
   * X/Twitter. Takes a `codeVerifier` the other two do not, because X supports only
   * authorization-code **with PKCE**: the verifier is generated in this browser and never leaves
   * it until now, which is the whole proof that the client finishing the flow is the one that
   * started it. The API cannot complete the exchange without it.
   */
  verifyTwitter: (code: string, redirectUri: string, codeVerifier: string) => Promise<void>;
}

export const useLogin = (): UseLoginResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Back from the next app restores this page from the back-forward cache mid-"signing in": give the
  // buttons back, since nothing is in flight any more.
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsLoading(false);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);
  const domain = apexDomain();
  // Never `next` as given: see `safeNext` — it was a session-theft link.
  const fallbackUrl = webUrl('settings');
  const nextUrl = safeNext(useParam('next', fallbackUrl), domain, fallbackUrl);
  const { track } = useTracker();
  // Shared with `Login` — `useLanguage` memoises the request, so this is not a second fetch.
  const { copy } = useLanguage();

  const verifyLinkedIn = useCallback(
    async (code: string, redirectUri: string) => {
      setIsLoading(true);
      setError(null);

      try {
        track({ name: LoginEvents.PAGE_VIEW, category: AUTHENTICATION });

        // `openSession` finishes a sign-out this page's mount started first, or it would clear the
        // cookies this POST sets.
        const session = await signIn(
          {
            provider: 'linkedin',
            code,
            redirect_uri: redirectUri,
          },
          copy,
          track,
        );
        // A 200 that names nobody signed nobody in; do not navigate on it.
        if (!session?.email) throw new Error(copy.failed);

        track({ name: LoginEvents.SUCCESS, category: AUTHENTICATION });

        go(nextUrl);
      } catch (err) {
        // Only a failure gives the buttons back: on success the page is navigating away, and must
        // keep saying so until the next app paints.
        setIsLoading(false);
        console.error('LinkedIn session exchange failed:', err);
        setError(catchMessage(err, copy));
        track({
          name: LoginEvents.OAUTH_ERROR,
          category: AUTHENTICATION,
          label: err instanceof Error ? err.message.slice(0, 120) : 'unknown',
        });
      }
    },
    [nextUrl, track, copy],
  );

  const verifyFacebook = useCallback(
    async (code: string, redirectUri: string) => {
      setIsLoading(true);
      setError(null);

      try {
        track({ name: LoginEvents.PAGE_VIEW, category: AUTHENTICATION });

        // `openSession` finishes a sign-out this page's mount started first, or it would clear the
        // cookies this POST sets.
        const session = await signIn(
          {
            provider: 'facebook',
            code,
            redirect_uri: redirectUri,
          },
          copy,
          track,
        );
        // A 200 that names nobody signed nobody in; do not navigate on it.
        if (!session?.email) throw new Error(copy.failed);

        track({ name: LoginEvents.SUCCESS, category: AUTHENTICATION });

        go(nextUrl);
      } catch (err) {
        // Only a failure gives the buttons back: on success the page is navigating away, and must
        // keep saying so until the next app paints.
        setIsLoading(false);
        console.error('Facebook session exchange failed:', err);
        setError(catchMessage(err, copy));
        track({
          name: LoginEvents.OAUTH_ERROR,
          category: AUTHENTICATION,
          label: err instanceof Error ? err.message.slice(0, 120) : 'unknown',
        });
      }
    },
    [nextUrl, track, copy],
  );

  const verify = useCallback(
    async (token: string) => {
      setIsLoading(true);
      setError(null);

      try {
        // Track login attempt
        track({ name: LoginEvents.PAGE_VIEW, category: AUTHENTICATION });

        // `openSession` finishes a sign-out this page's mount started first, or it would clear the
        // cookies this POST sets.
        const session = await signIn({ token }, copy, track);
        // A 200 that names nobody signed nobody in; do not navigate on it.
        if (!session?.email) throw new Error(copy.failed);

        // Track login success
        track({ name: LoginEvents.SUCCESS, category: AUTHENTICATION });

        // Redirect to the next URL
        go(nextUrl);
      } catch (err) {
        // Only a failure gives the buttons back: on success the page is navigating away, and must
        // keep saying so until the next app paints.
        setIsLoading(false);
        console.error('Google session exchange failed:', err);
        setError(catchMessage(err, copy));
        track({
          name: LoginEvents.OAUTH_ERROR,
          category: AUTHENTICATION,
          label: err instanceof Error ? err.message.slice(0, 120) : 'unknown',
        });
      }
    },
    [nextUrl, track, copy],
  );

  const verifyTwitter = useCallback(
    async (code: string, redirectUri: string, codeVerifier: string) => {
      setIsLoading(true);
      setError(null);

      try {
        track({ name: LoginEvents.PAGE_VIEW, category: AUTHENTICATION });

        // `openSession` finishes a sign-out this page's mount started first, or it would clear the
        // cookies this POST sets.
        const session = await signIn(
          {
            provider: 'twitter',
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          },
          copy,
          track,
        );
        // A 200 that names nobody signed nobody in; do not navigate on it.
        if (!session?.email) throw new Error(copy.failed);

        track({ name: LoginEvents.SUCCESS, category: AUTHENTICATION });

        go(nextUrl);
      } catch (err) {
        // Only a failure gives the buttons back: on success the page is navigating away, and must
        // keep saying so until the next app paints.
        setIsLoading(false);
        console.error('X session exchange failed:', err);
        setError(catchMessage(err, copy));
        track({
          name: LoginEvents.OAUTH_ERROR,
          category: AUTHENTICATION,
          label: err instanceof Error ? err.message.slice(0, 120) : 'unknown',
        });
      }
    },
    [nextUrl, track, copy],
  );

  return {
    isLoading,
    error,
    verify,
    verifyLinkedIn,
    verifyFacebook,
    verifyTwitter,
  };
};
